import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ScanningScreenProps {
  preview: string;
}

export const ScanningScreen: React.FC<ScanningScreenProps> = ({ preview }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const messages = [
    'Scanning the image...',
    'Checking the details...',
    'Looking for AI signs...',
    'Verifying authenticity...',
    'Almost done...',
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentMessageIndex(prev => (prev + 1) % messages.length);
    }, 1500);
    return () => clearTimeout(timer);
  }, [currentMessageIndex, messages.length]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      {/* Image Preview with Square Aspect, Corner Box, and Scanning Line */}
      <div className="relative w-full max-w-sm mx-auto aspect-square bg-white rounded-3xl overflow-hidden border-2 border-[#D0D5DD] shadow-lg">
        {/* Rounded Corner Box - Only visible corners */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Top-left corner */}
          <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-[#1F4FFF] rounded-tl-2xl"></div>
          {/* Top-right corner */}
          <div className="absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 border-[#1F4FFF] rounded-tr-2xl"></div>
          {/* Bottom-left corner */}
          <div className="absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-[#1F4FFF] rounded-bl-2xl"></div>
          {/* Bottom-right corner */}
          <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-[#1F4FFF] rounded-br-2xl"></div>
        </div>

        {/* Image */}
        <img
          src={preview}
          alt="Scanning"
          className="w-full h-full object-cover"
        />

        {/* Flat Horizontal Scanning Line - Alternating Direction */}
        <motion.div
          animate={{ top: ['0%', '100%', '100%', '0%'] }}
          transition={{ 
            duration: 5, 
            repeat: Infinity, 
            ease: 'linear',
            times: [0, 0.45, 0.55, 1]
          }}
          className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-[#1F4FFF] to-transparent"
        />
      </div>

      {/* Sequential Message Animation */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="bg-white rounded-2xl p-6 border border-[#D0D5DD] min-h-24 flex items-center justify-center"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentMessageIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            <p className="text-lg font-medium text-[#0A1A2F]">
              {messages[currentMessageIndex]}
            </p>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default ScanningScreen;
